import type { HomeEconomy, HomeWallet } from "@/home/economy";
import { HEALTH_POTION_DEFAULT_COIN_PRICE } from "@/home/health-potions";

export type ManaPotionFulfillmentKind =
  | "mana_potion_small"
  | "mana_potion_medium"
  | "mana_potion_large";

export const MANA_POTION_TIERS = ["small", "medium", "large"] as const;
export type ManaPotionTier = (typeof MANA_POTION_TIERS)[number];

export const MANA_POTION_RESTORE_BY_TIER: Record<ManaPotionTier, number> = {
  small: 20,
  medium: 80,
  large: 150,
};

export const MANA_POTION_LABEL_TH: Record<ManaPotionTier, string> = {
  small: "ยามานาเล็ก",
  medium: "ยามานากลาง",
  large: "ยามานาใหญ่",
};

export const MANA_POTION_SHORT_LABEL: Record<ManaPotionTier, string> = {
  small: "S",
  medium: "M",
  large: "L",
};

/** Same coin prices as health potions. */
export const MANA_POTION_DEFAULT_COIN_PRICE: Record<ManaPotionTier, number> = {
  small: HEALTH_POTION_DEFAULT_COIN_PRICE.small,
  medium: HEALTH_POTION_DEFAULT_COIN_PRICE.medium,
  large: HEALTH_POTION_DEFAULT_COIN_PRICE.large,
};

export const MANA_POTION_SKU_BY_TIER: Record<ManaPotionTier, string> = {
  small: "home.mana_potion.small.v1",
  medium: "home.mana_potion.medium.v1",
  large: "home.mana_potion.large.v1",
};

export type ManaPotionStashItemId =
  | "mana_potion_small"
  | "mana_potion_medium"
  | "mana_potion_large";

export function tierFromManaStashItemId(itemId: string): ManaPotionTier | null {
  if (itemId === "mana_potion_small") return "small";
  if (itemId === "mana_potion_medium") return "medium";
  if (itemId === "mana_potion_large") return "large";
  return null;
}

export function stashItemIdForManaTier(tier: ManaPotionTier): ManaPotionStashItemId {
  if (tier === "small") return "mana_potion_small";
  if (tier === "medium") return "mana_potion_medium";
  return "mana_potion_large";
}

export function fulfillmentForManaTier(tier: ManaPotionTier): ManaPotionFulfillmentKind {
  if (tier === "small") return "mana_potion_small";
  if (tier === "medium") return "mana_potion_medium";
  return "mana_potion_large";
}

export function tierFromManaFulfillment(fulfillment: string): ManaPotionTier | null {
  if (fulfillment === "mana_potion_small") return "small";
  if (fulfillment === "mana_potion_medium") return "medium";
  if (fulfillment === "mana_potion_large") return "large";
  return null;
}

export function walletKeyForManaTier(tier: ManaPotionTier): keyof HomeWallet {
  if (tier === "small") return "manaPotionsSmall";
  if (tier === "medium") return "manaPotionsMedium";
  return "manaPotionsLarge";
}

export function manaPotionCount(economy: HomeEconomy, tier: ManaPotionTier): number {
  return economy.wallet[walletKeyForManaTier(tier)];
}

export function totalManaPotions(wallet: HomeWallet): number {
  return wallet.manaPotionsSmall + wallet.manaPotionsMedium + wallet.manaPotionsLarge;
}

export function addManaPotionsToWallet(
  wallet: HomeWallet,
  tier: ManaPotionTier,
  quantity: number,
): HomeWallet {
  const key = walletKeyForManaTier(tier);
  return {
    ...wallet,
    [key]: Math.max(0, wallet[key] + Math.floor(quantity)),
  };
}

export function restoreAmountForManaTier(tier: ManaPotionTier): number {
  return MANA_POTION_RESTORE_BY_TIER[tier];
}
